/**
 * Centralized keyboard shortcut registry.
 *
 * One source of truth for every named keyboard shortcut surfaced in the
 * Otto UI. Each shortcut declares:
 *   - `parts`: the canonical key combo, where 'Ctrl' is the *primary*
 *     modifier (becomes ⌘ on Mac via `formatKeyCombo`'s mapping).
 *   - `mac` (optional): an explicit Mac override when the actual key
 *     differs (e.g. Format Workflow uses ⇧⌥F on Win/Linux but ⇧⌘F on
 *     Mac to match Apple's HIG and VS Code's "Format Document").
 *   - `description`: human-readable label rendered in the shortcuts
 *     dialog and used as the source of truth for tooltip text.
 *   - `category`: which section of the shortcuts dialog this lives in.
 *   - `matches` (optional): a `KeyboardEvent` predicate used by handler
 *     hooks. When omitted, callers continue to roll their own matchers
 *     — the registry's primary purpose is consistent *display* text.
 *
 * Why not auto-derive matchers from `parts`? Several handlers (canvas
 * search, undo/redo, format) need bespoke logic — Mac's Option key
 * mutates `event.key` into special glyphs (e.g. ⇧⌥F sends "Ï"), which
 * forces handlers to key off `event.code` instead. Forcing that detail
 * into a generic matcher would either be wrong in subtle ways or
 * require so many opt-outs that it's clearer to write the matcher per
 * shortcut where needed.
 */

import { formatKeyCombo, IS_MAC, isPrimaryMod } from './platformKeys';

/** Logical identifiers for every shortcut surfaced in the UI. */
export type ShortcutId =
  // ── Workflow ─────────────────────────────────────────────────────
  | 'save'
  | 'run'
  | 'publish'
  | 'undo'
  | 'redo'
  // ── View ─────────────────────────────────────────────────────────
  | 'toggleCode'
  | 'toggleSidebar'
  | 'toggleAssistant'
  | 'canvasSearch'
  | 'formatWorkflow'
  | 'switchToDesigner'
  | 'switchToMonitoring'
  // ── Edit ─────────────────────────────────────────────────────────
  | 'addOperation'
  | 'duplicateNode'
  | 'selectAll'
  | 'copyNode'
  | 'cutNode'
  | 'pasteNode'
  | 'deleteNode'
  // ── Navigation ───────────────────────────────────────────────────
  | 'commandPalette'
  | 'openSettings'
  | 'shortcutsHelp'
  | 'closeOverlay';

export type ShortcutCategory =
  | 'workflow'
  | 'view'
  | 'edit'
  | 'navigation';

interface ShortcutDef {
  /** Display description (also used as tooltip source-of-truth). */
  description: string;
  /** Canonical key parts. Use 'Ctrl' for the primary modifier (→ ⌘ on Mac). */
  parts: readonly string[];
  /** Explicit Mac override when keys differ from non-Mac. */
  mac?: readonly string[];
  /** Section in the shortcuts dialog. */
  category: ShortcutCategory;
  /** Optional KeyboardEvent matcher for handler hooks. */
  matches?: (event: KeyboardEvent) => boolean;
}

// Helpers used by matcher predicates ───────────────────────────────────

/**
 * True when the key produces character `c`, matched on `event.code`
 * (layout-independent). Use this for letter shortcuts that have to
 * survive the Mac Option-key mutation (⌥ + letter sends a special
 * character in `event.key`, but `event.code` stays "KeyX").
 */
const isCode = (e: KeyboardEvent, code: string): boolean => e.code === code;

// Registry ────────────────────────────────────────────────────────────

const SHORTCUTS = {
  // ── Workflow ─────────────────────────────────────────────────────
  save: {
    description: 'Save workflow',
    parts: ['Ctrl', 'S'],
    category: 'workflow',
    matches: (e) => isPrimaryMod(e) && !e.shiftKey && !e.altKey && isCode(e, 'KeyS'),
  },
  run: {
    description: 'Run workflow',
    parts: ['Ctrl', 'Enter'],
    category: 'workflow',
    matches: (e) => isPrimaryMod(e) && !e.shiftKey && !e.altKey && e.key === 'Enter',
  },
  publish: {
    description: 'Publish draft workflow',
    parts: ['Ctrl', 'Shift', 'P'],
    category: 'workflow',
  },
  undo: {
    description: 'Undo',
    parts: ['Ctrl', 'Z'],
    category: 'workflow',
    matches: (e) => isPrimaryMod(e) && !e.shiftKey && isCode(e, 'KeyZ'),
  },
  redo: {
    description: 'Redo',
    parts: ['Ctrl', 'Shift', 'Z'],
    category: 'workflow',
    matches: (e) =>
      // Ctrl+Shift+Z everywhere, or Ctrl+Y on Win/Linux as the alt
      (isPrimaryMod(e) && e.shiftKey && isCode(e, 'KeyZ')) ||
      (isPrimaryMod(e) && !e.shiftKey && isCode(e, 'KeyY')),
  },

  // ── View ─────────────────────────────────────────────────────────
  toggleCode: {
    description: 'Toggle code panel',
    parts: ['Ctrl', 'E'],
    category: 'view',
    matches: (e) => isPrimaryMod(e) && !e.altKey && isCode(e, 'KeyE'),
  },
  toggleSidebar: {
    description: 'Toggle sidebar',
    // ⌘B / Ctrl+B — VS Code, Linear, Notion, Slack all use this for the
    // primary side panel. Plain (no Shift / Alt) so it's quick to press.
    parts: ['Ctrl', 'B'],
    category: 'view',
    matches: (e) => isPrimaryMod(e) && !e.shiftKey && !e.altKey && isCode(e, 'KeyB'),
  },
  toggleAssistant: {
    description: 'Toggle Copilot panel',
    parts: ['Ctrl', 'Shift', 'A'],
    category: 'view',
    matches: (e) => isPrimaryMod(e) && e.shiftKey && !e.altKey && isCode(e, 'KeyA'),
  },
  canvasSearch: {
    description: 'Search the canvas',
    parts: ['Ctrl', 'F'],
    category: 'view',
    // Accept EITHER modifier (but not both) on either platform.
    // ⌘F / Ctrl+F is the universal find chord (browsers, IDEs, every
    // major desktop app) and users press whichever modifier feels
    // natural. Neither variant shadows a native browser/OS chord for
    // F that we'd want to preserve here (the page-find we'd suppress
    // anyway). This also keeps the matcher platform-agnostic in E2E
    // tests where chromium may report any host OS.
    matches: (e) => {
      const hasPrimaryMod = (e.metaKey || e.ctrlKey) && !(e.metaKey && e.ctrlKey);
      return hasPrimaryMod && !e.shiftKey && !e.altKey && isCode(e, 'KeyF');
    },
  },
  formatWorkflow: {
    description: 'Auto-arrange nodes',
    // ⇧⌥F matches VS Code's Format Document on Win/Linux.
    parts: ['Shift', 'Alt', 'F'],
    // ⇧⌘F is Apple's standard Format Document binding (⌥ as a dead
    // key on Mac mutates event.key into "Ï" so we also support ⇧⌘F
    // as a keystroke users can actually type without a stuck modifier).
    mac: ['Shift', 'Cmd', 'F'],
    category: 'view',
    matches: (e) => {
      if (!isCode(e, 'KeyF')) return false;
      // Mac path: ⇧⌘F. Gated on IS_MAC so a Win/Linux user hitting
      // Ctrl+Shift+F (a common browser/IDE binding) doesn't accidentally
      // auto-arrange the canvas. Require metaKey && !ctrlKey so the
      // chord is unambiguously the Mac one.
      if (IS_MAC && e.metaKey && !e.ctrlKey) {
        return e.shiftKey && !e.altKey;
      }
      // Win/Linux path: ⇧⌥F (no ctrl, no meta).
      return e.shiftKey && e.altKey && !e.ctrlKey && !e.metaKey;
    },
  },
  switchToDesigner: {
    description: 'Switch to Designer tab',
    parts: ['Ctrl', 'Shift', '1'],
    category: 'view',
    matches: (e) => isPrimaryMod(e) && e.shiftKey && e.code === 'Digit1',
  },
  switchToMonitoring: {
    description: 'Switch to Monitoring tab',
    parts: ['Ctrl', 'Shift', '2'],
    category: 'view',
    matches: (e) => isPrimaryMod(e) && e.shiftKey && e.code === 'Digit2',
  },

  // ── Edit ─────────────────────────────────────────────────────────
  addOperation: {
    description: 'Add operation',
    // Bare 'A' — no modifier. Same key on every platform.
    //
    // Explicitly rejects BOTH ctrl and meta (not just the primary mod)
    // so the non-primary modifier — Ctrl on macOS, Meta on Win/Linux —
    // doesn't satisfy `!isPrimaryMod(e)` and let bare-A misfire while
    // that key is held.
    parts: ['A'],
    category: 'edit',
    matches: (e) =>
      !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey && isCode(e, 'KeyA'),
  },
  duplicateNode: {
    description: 'Duplicate selected node',
    parts: ['Ctrl', 'D'],
    category: 'edit',
    matches: (e) => isPrimaryMod(e) && !e.shiftKey && isCode(e, 'KeyD'),
  },
  selectAll: {
    description: 'Select all nodes',
    parts: ['Ctrl', 'A'],
    category: 'edit',
    matches: (e) => isPrimaryMod(e) && !e.shiftKey && isCode(e, 'KeyA'),
  },
  copyNode: {
    description: 'Copy selected node',
    parts: ['Ctrl', 'C'],
    category: 'edit',
  },
  cutNode: {
    description: 'Cut selected node',
    parts: ['Ctrl', 'X'],
    category: 'edit',
  },
  pasteNode: {
    description: 'Paste node',
    parts: ['Ctrl', 'V'],
    category: 'edit',
  },
  deleteNode: {
    description: 'Delete selected node',
    parts: ['Delete'],
    category: 'edit',
  },

  // ── Navigation ───────────────────────────────────────────────────
  commandPalette: {
    description: 'Open command palette',
    parts: ['Ctrl', 'K'],
    category: 'navigation',
    matches: (e) => isPrimaryMod(e) && !e.shiftKey && !e.altKey && isCode(e, 'KeyK'),
  },
  openSettings: {
    description: 'Open settings',
    parts: ['Ctrl', ','],
    category: 'navigation',
    matches: (e) =>
      isPrimaryMod(e) && !e.shiftKey && !e.altKey && (e.key === ',' || e.code === 'Comma'),
  },
  shortcutsHelp: {
    description: 'Show keyboard shortcuts',
    parts: ['?'],
    category: 'navigation',
  },
  closeOverlay: {
    description: 'Close any open dialog or panel',
    parts: ['Esc'],
    category: 'navigation',
  },
} satisfies Record<ShortcutId, ShortcutDef>;

// Public API ──────────────────────────────────────────────────────────

/** Return the raw definition for a shortcut. */
export function getShortcut(id: ShortcutId): ShortcutDef {
  return SHORTCUTS[id];
}

/**
 * Return the platform-formatted label for a shortcut.
 *
 *   getShortcutLabel('save')           // '⌘S' on Mac, 'Ctrl+S' elsewhere
 *   getShortcutLabel('formatWorkflow') // '⇧⌘F' on Mac, 'Shift+Alt+F' elsewhere
 */
export function getShortcutLabel(id: ShortcutId): string {
  const def = getShortcut(id);
  const parts = IS_MAC && def.mac ? def.mac : def.parts;
  return formatKeyCombo(parts);
}

// Mac glyph map for per-part rendering. Mirrors the mapping used inside
// `formatKeyCombo`; kept as a separate constant here so we can render
// each part as its own keycap in the help dialog.
const MAC_GLYPHS: Record<string, string> = {
  Ctrl: '⌘',
  Cmd: '⌘',
  Meta: '⌘',
  Shift: '⇧',
  Alt: '⌥',
  Option: '⌥',
  Control: '⌃',
  Enter: '⏎',
  Return: '⏎',
  Backspace: '⌫',
  Delete: '⌦',
  Escape: 'Esc',
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
};

/**
 * Return the parts the user actually presses on the current platform.
 * Useful for the shortcuts dialog where each part is rendered as its
 * own keycap.
 */
export function getShortcutParts(id: ShortcutId): readonly string[] {
  const def = getShortcut(id);
  const parts = IS_MAC && def.mac ? def.mac : def.parts;
  if (!IS_MAC) return parts;
  return parts.map((p) => MAC_GLYPHS[p] ?? p);
}

/**
 * Test whether a `KeyboardEvent` matches the given shortcut. Returns
 * false (and warns in development) if the shortcut has no matcher
 * defined — those shortcuts are display-only.
 */
export function matchesShortcut(event: KeyboardEvent, id: ShortcutId): boolean {
  const def = getShortcut(id);
  if (!def.matches) {
    if (import.meta.env.DEV) {
      console.warn(`[shortcuts] '${id}' has no matcher; cannot match events`);
    }
    return false;
  }
  return def.matches(event);
}

/** Group shortcuts by category for the help dialog. */
export function getShortcutsByCategory(): Record<ShortcutCategory, ShortcutId[]> {
  const result: Record<ShortcutCategory, ShortcutId[]> = {
    workflow: [],
    view: [],
    edit: [],
    navigation: [],
  };
  for (const id of Object.keys(SHORTCUTS) as ShortcutId[]) {
    result[getShortcut(id).category].push(id);
  }
  return result;
}
